import { useState, useRef } from "react"
import { Link } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { PawPrint, Leaf, Lock, Share2 } from "lucide-react"
import { PortraitTeaserCard } from "~/components/PortraitTeaserCard"
import { archetypeImageUrl } from "~/utils/archetypeImageUrl"
import {
  formatRarityPrimaryLine,
  formatRaritySecondaryLine,
  getArchetypePopulationPercent,
} from "~/utils/archetypeRarity"
import { heroPortraitUrl, heroPortraitShareUrl } from "~/utils/heroPortraitUrl"
import type { DogPortraitInfo } from "~/api/auth"
import { MeetAskDynamoSection } from "~/components/MeetAskDynamoSection"
import { EverWonderedAskDynamoSection } from "~/components/EverWonderedAskDynamoSection"
import { ShareableCardModal } from "~/components/ShareableCardModal"
import type {
  AssessmentSessionResponse,
  ArchetypeProfile,
  ArchetypeSection,
} from "~/api/assessmentSessions"

type GuidanceTab = "daily" | "training" | "out"

interface CharacterCardProps {
  dogData: AssessmentSessionResponse
  sessionToken: string | null
  /** When true, hide signup funnel and show full DO/DON'T (logged-in full profile). */
  isAuthenticated?: boolean
  portrait?: DogPortraitInfo | null
}

export function CharacterCard({
  dogData,
  sessionToken,
  isAuthenticated = false,
  portrait = null,
}: CharacterCardProps) {
  const archetype = dogData.archetype
  if (!archetype) return null

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [guidanceTab, setGuidanceTab] = useState<GuidanceTab>("daily")

  const guidanceSection: ArchetypeSection =
    guidanceTab === "daily"
      ? archetype.daily_life
      : guidanceTab === "training"
        ? archetype.training
        : archetype.out_and_about
  const cardRef = useRef<HTMLDivElement>(null)

  function handleOpenShare() {
    setShareModalOpen(true)
  }

  function handleOpenDrawer() {
    setDrawerOpen(true)
  }

  const personalisedHeroUrl =
    portrait?.status === "ready" && portrait.url
      ? heroPortraitUrl(archetype.icon, portrait)
      : null
  const archetypeSymbolUrl = archetypeImageUrl(archetype.icon, "logos")
  const showShare = isAuthenticated && portrait?.status === "ready"
  const populationPercent = getArchetypePopulationPercent(archetype)

  return (
    <motion.div
      ref={cardRef}
      initial={{ scale: 0.95 }}
      animate={{ scale: 1 }}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.995 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="relative mx-auto w-full max-w-lg overflow-hidden rounded-2xl shadow-[0_22px_70px_4px_rgba(0,0,0,0.1)]"
    >
      {/* Parchment card background */}
      <div
        className="relative min-h-[400px] rounded-2xl px-6 py-8"
        style={{
          background: "radial-gradient(ellipse at center, #FFFDFB 0%, #F5F0E8 100%)",
        }}
      >
        {/* Noise overlay */}
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl opacity-[0.06] mix-blend-multiply"
          aria-hidden
        >
          <svg className="h-full w-full">
            <defs>
              <filter id="grain">
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.8"
                  numOctaves="4"
                  result="noise"
                />
                <feColorMatrix in="noise" type="saturate" values="0" />
              </filter>
            </defs>
            <rect width="100%" height="100%" filter="url(#grain)" fill="white" />
          </svg>
        </div>

        {/* Hero: personalised portrait when ready; otherwise archetype symbol (no stock artwork) */}
        <header className="relative flex flex-col items-center text-center">
          {personalisedHeroUrl ? (
            <div className="relative w-full max-h-[280px]">
              <div className="relative w-full rounded-[44px] border border-[var(--color-gold-300)]/70 bg-gradient-to-br from-white/90 via-[#f8f1df]/80 to-[#e2cfac]/70 p-1 shadow-[0_28px_80px_rgba(0,0,0,0.25)]">
                <div
                  className="w-full overflow-hidden rounded-[40px] border border-white/40"
                  style={{ aspectRatio: "16/10" }}
                >
                  <img
                    src={personalisedHeroUrl}
                    alt={`Portrait for ${archetype.name} archetype`}
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                <div
                  className="seal-shimmer absolute top-0 right-0 z-10 flex h-14 w-14 translate-x-1/3 -translate-y-1/3 items-center justify-center rounded-full border border-[rgba(212,175,55,0.7)] bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.95),transparent_55%),rgba(255,248,224,0.7)] backdrop-blur-md shadow-[0_18px_45px_rgba(0,0,0,0.35)]"
                  aria-hidden
                >
                  <img
                    src={archetypeSymbolUrl}
                    alt=""
                    className="logo-badge-img h-8 w-8 object-contain"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-28 w-28 items-center justify-center rounded-xl border-2 border-[var(--color-gold-300)] bg-gradient-to-br from-white/95 via-[#f8f1df]/90 to-[#e2cfac]/80 p-3 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
              <img
                src={archetypeSymbolUrl}
                alt={`${archetype.name} symbol`}
                className="h-full w-full object-contain"
              />
            </div>
          )}

          <h1 className="mt-3 text-3xl font-bold text-[var(--color-text-primary)] sm:mt-4">
            {dogData.dog_name} is{" "}
            <span
              className="text-metallic-gold text-metallic-gold-embossed font-serif"
              style={{ fontFamily: "var(--font-serif)" }}
            >
              {archetype.name.toUpperCase()}
            </span>
          </h1>
          <p className="mt-2 text-center text-sm text-[var(--color-text-secondary)]">
            {archetype.hook_primary}
          </p>
          {archetype.hook_secondary && (
            <p className="mt-1.5 text-center text-sm text-[var(--color-text-secondary)]">
              {archetype.hook_secondary}
            </p>
          )}
          <p className="mt-4 text-center text-sm font-semibold text-[var(--color-text-primary)]">
            {formatRarityPrimaryLine(archetype.name, populationPercent)}
          </p>
          <p className="mt-1 text-center text-sm text-[var(--color-text-secondary)]">
            {formatRaritySecondaryLine(populationPercent)}
          </p>
          {!isAuthenticated && archetype.ask_dynamo && (
            <div className="mt-6 w-full text-left">
              <MeetAskDynamoSection
                dogName={dogData.dog_name}
                exampleQuestions={archetype.ask_dynamo.example_questions}
                sessionToken={sessionToken}
              />
            </div>
          )}
          {!isAuthenticated && <PortraitTeaserCard />}
          {showShare && (
            <button
              type="button"
              onClick={handleOpenShare}
              className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[var(--color-divider)] bg-white/70 px-4 py-2 text-sm font-medium text-[var(--color-text-primary)] transition hover:bg-white"
            >
              <Share2 size={15} />
              Share {dogData.dog_name}&apos;s {archetype.name}
            </button>
          )}
        </header>

        {/* Guidance tabs + DO/DON'T — guests see real layout behind semi-transparent unlock card; tip lines blurred */}
        <div className="relative mt-4 overflow-hidden rounded-xl sm:mt-6">
          <div
            className={!isAuthenticated ? "pointer-events-none select-none" : undefined}
            aria-hidden={!isAuthenticated || undefined}
          >
            <div
              className="flex gap-1 rounded-xl border border-[var(--color-divider)] bg-white/50 p-1 backdrop-blur-sm"
              role="tablist"
              aria-label="Guidance context"
            >
              {(
                [
                  { id: "daily" as const, label: "Daily Life" },
                  { id: "training" as const, label: "Training" },
                  { id: "out" as const, label: "Out & About" },
                ] as const
              ).map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  tabIndex={!isAuthenticated ? -1 : undefined}
                  aria-selected={guidanceTab === id}
                  onClick={() => setGuidanceTab(id)}
                  className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
                    guidanceTab === id
                      ? "bg-[var(--color-surface-card)] text-[var(--color-text-primary)] shadow-sm"
                      : "text-[var(--color-text-secondary)] hover:bg-white/60"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="relative mt-3 grid gap-2 sm:mt-4 sm:grid-cols-2">
              <DoDontCard
                type="do"
                title="DO"
                items={guidanceSection.do}
                icon={PawPrint}
                guestObscured={!isAuthenticated}
              />
              <DoDontCard
                type="dont"
                title="DON'T"
                items={guidanceSection.dont}
                icon={Leaf}
                guestObscured={!isAuthenticated}
              />
            </div>
          </div>

        </div>

        {isAuthenticated && (
          <EverWonderedAskDynamoSection dogName={dogData.dog_name} />
        )}

        {/* At a Glance trigger */}
        <div className="relative mt-8 sm:mt-10">
          <button
            type="button"
            onClick={handleOpenDrawer}
            className="w-full rounded-xl border border-[var(--color-divider)] bg-white/60 px-4 py-3 text-sm font-medium text-[var(--color-text-primary)] backdrop-blur-sm transition hover:bg-white/80"
          >
            Go deeper — routines, challenges &amp; weekly tips
          </button>
        </div>
      </div>

      {/* Bottom sheet */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              key="sheet-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDrawerOpen(false)}
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
              aria-hidden
            />
            <motion.div
              key="sheet-panel"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 35 }}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={0.2}
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 300) setDrawerOpen(false)
              }}
              className="fixed bottom-0 left-0 right-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-[var(--color-surface-card)] shadow-xl"
            >
              <div className="sticky top-0 flex justify-center border-b border-[var(--color-divider)] bg-[var(--color-surface-card)] py-2">
                <div className="h-1.5 w-12 rounded-full bg-[var(--color-divider)]" aria-hidden />
              </div>
              <div className="p-6 pb-8">
                <DrawerContent
                  archetype={archetype}
                  sessionToken={sessionToken}
                  dogName={dogData.dog_name}
                  isAuthenticated={isAuthenticated}
                  onClose={() => setDrawerOpen(false)}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <ShareableCardModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        data={dogData}
        personalizedPortraitUrl={
          portrait?.status === "ready"
            ? heroPortraitShareUrl(archetype.icon, portrait)
            : null
        }
      />
    </motion.div>
  )
}

function DoDontCard({
  type,
  title,
  items,
  icon: Icon,
  guestObscured = false,
}: {
  type: "do" | "dont"
  title: string
  items: string[]
  icon: React.ComponentType<{ className?: string; size?: number }>
  /** When true, list rows use lock icons; tip text is blurred so structure stays visible but unreadable. */
  guestObscured?: boolean
}) {
  const isDo = type === "do"
  return (
    <motion.div
      whileHover={{ scale: guestObscured ? 1 : 1.01 }}
      transition={{ type: "spring", stiffness: 220, damping: 18 }}
      className={`glass-card relative rounded-xl border border-white/40 border-t-[1px] border-l-[1px] p-3 ${
        isDo ? "bg-emerald-50/20" : "bg-amber-50/20"
      }`}
    >
      <h2
        className={`mb-1.5 text-sm font-semibold ${
          isDo ? "text-emerald-800" : "text-amber-800"
        }`}
      >
        {title}
      </h2>
      <ul className="flex flex-col gap-1.5">
        {items.map((item, idx) => (
          <li
            key={idx}
            className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]"
          >
            {guestObscured ? (
              <Lock
                className={`mt-0.5 shrink-0 ${isDo ? "text-emerald-700/80" : "text-amber-700/80"}`}
                size={15}
                strokeWidth={1.6}
              />
            ) : (
              <Icon
                className={`mt-0.5 shrink-0 ${isDo ? "text-emerald-700" : "text-amber-700"}`}
                size={16}
                strokeWidth={1.6}
              />
            )}
            <span
              className={
                guestObscured
                  ? "character-card-guest-tip-text inline-block max-w-full blur-[3.5px] opacity-[0.82] saturate-50"
                  : undefined
              }
            >
              {item}
            </span>
          </li>
        ))}
      </ul>
    </motion.div>
  )
}

function DrawerContent({
  archetype,
  sessionToken,
  dogName,
  isAuthenticated,
  onClose,
}: {
  archetype: ArchetypeProfile
  sessionToken: string | null
  dogName: string
  isAuthenticated: boolean
  onClose: () => void
}) {
  return (
    <div className="flex flex-col gap-6">
      {archetype.deep_dives.length > 0 && (
        <section>
          <h2 className="mb-3 text-base font-semibold text-[var(--color-text-primary)]">
            Deep dives
          </h2>
          <div className="flex flex-col gap-3">
            {archetype.deep_dives.map((dive, index) => (
              <details
                key={index}
                className="rounded-lg border border-[var(--color-divider)] bg-white/60 px-3 py-2 backdrop-blur-sm"
              >
                <summary className="cursor-pointer text-sm font-semibold text-[var(--color-text-primary)]">
                  {dive.title}
                </summary>
                <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
                  {dive.content}
                </p>
              </details>
            ))}
          </div>
        </section>
      )}
      {!isAuthenticated && (
        <section className="rounded-lg bg-white/60 p-4 backdrop-blur-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
            Free guidance
          </p>
          <h2 className="mt-1 text-base font-semibold text-[var(--color-text-primary)]">
            {archetype.cta_signup.headline}
          </h2>
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
            {archetype.cta_signup.text}
          </p>
          <Link
            to="/signup"
            state={sessionToken ? { assessmentSessionToken: sessionToken, dogName } : undefined}
            onClick={onClose}
            className="mt-4 block w-full rounded-lg bg-[var(--color-text-primary)] px-4 py-3 text-center text-sm font-medium text-[var(--color-surface-card)] transition hover:opacity-90"
          >
            {archetype.cta_signup.button_label}
          </Link>
        </section>
      )}
    </div>
  )
}
